import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Trash2 } from 'lucide-react';
import { CrmNegocioComentario } from '@/types/crm';
import { crmComentarioService } from '@/services/crmApi';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface CrmNegocioComentariosProps {
  negocioId: string;
  comentarios: CrmNegocioComentario[];
  onComentariosChange: (comentarios: CrmNegocioComentario[]) => void;
}

function formatDataHora(iso?: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function CrmNegocioComentarios({ negocioId, comentarios, onComentariosChange }: CrmNegocioComentariosProps) {
  const { user } = useAuth();
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [excluindoId, setExcluindoId] = useState<string | null>(null);

  const handleEnviar = async () => {
    if (!texto.trim()) return;
    setEnviando(true);
    const result = await crmComentarioService.create(negocioId, texto.trim());
    setEnviando(false);
    if (result.success) {
      onComentariosChange([...comentarios, result.data]);
      setTexto('');
    } else {
      toast.error(result.error || 'Erro ao comentar');
    }
  };

  const handleExcluir = async (comentario: CrmNegocioComentario) => {
    setExcluindoId(comentario.id);
    const result = await crmComentarioService.delete(negocioId, comentario.id);
    setExcluindoId(null);
    if (result.success) {
      onComentariosChange(comentarios.filter((c) => c.id !== comentario.id));
    } else {
      toast.error(result.error || 'Erro ao excluir comentário');
    }
  };

  const podeExcluir = (comentario: CrmNegocioComentario) =>
    comentario.usuario_id === user?.id || user?.tipo !== 'comum';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4" />
          Comentários
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Textarea
            placeholder="Escreva um comentário..."
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            className="min-h-[60px] flex-1"
          />
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={handleEnviar} disabled={enviando || !texto.trim()}>
            {enviando ? 'Enviando...' : 'Comentar'}
          </Button>
        </div>

        <div className="space-y-2">
          {comentarios.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhum comentário ainda</p>
          )}
          {comentarios.map((comentario) => (
            <div key={comentario.id} className="rounded-md border p-3 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{comentario.usuario?.nome}</p>
                  <p className="text-xs text-muted-foreground">{formatDataHora(comentario.createdAt)}</p>
                </div>
                {podeExcluir(comentario) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={excluindoId === comentario.id}
                    onClick={() => handleExcluir(comentario)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                )}
              </div>
              <p className="mt-1 whitespace-pre-wrap">{comentario.texto}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
